/**
 * DailyRewardsChart — Contract MR-v1
 *
 * Rendert die data.daily-Serie (taeglicher HNT-Reward-Verlauf) als Line-Chart.
 * Folgt dem Projekt-Muster aus dashboard/RewardsLineChart.tsx (recharts).
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import type { DailyReward } from './useMonthlyReport'
import { buildFullMonthSeries } from './dailyRewardsUtils'
import { isPeriodInRewardDataGap } from '../../lib/rewardDataGap'
import { RewardDataGapNotice } from '../../components/RewardDataGapNotice'

const HNT_COLOR = '#22c55e'

// --- Hilfsfunktionen ---
function shortDate(dateStr: string): string {
  // '2026-05-10' → '10.05'
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  return `${parts[2]}.${parts[1]}`
}

type TooltipPayload = {
  payload?: DailyReward
}

function ChartTooltip({ payload }: { active?: boolean; payload?: TooltipPayload[] }) {
  if (!payload || payload.length === 0) return null
  const entry = payload[0]?.payload
  if (!entry) return null
  return (
    <div
      className="card card-pad"
      style={{ padding: '8px 12px', fontSize: 12, minWidth: 140 }}
      aria-live="polite"
    >
      <div className="muted" style={{ marginBottom: 4 }}>{entry.date}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: HNT_COLOR, flexShrink: 0 }} />
        <span className="num-sm c-hnt">
          {entry.rewards_hnt != null ? entry.rewards_hnt.toFixed(4) : '—'} HNT
        </span>
      </div>
    </div>
  )
}

type Props = {
  daily: DailyReward[]
  /** Report-Periode als "YYYY-MM". Wenn gesetzt, wird die Serie auf den
   * kompletten Kalendermonat aufgefuellt (fehlende Tage = 0 HNT), statt nur
   * die vom Backend gelieferten (sparsen) Reward-Tage zu plotten. Vorher
   * brach die X-Achse am letzten vorhandenen Datenpunkt ab und wirkte damit
   * bei Monaten mit Datenlücken fälschlich "abgeschnitten". */
  period?: string
  /** Vergangener (abgeschlossener) Monat -> "Final"-Badge wie in der Design-Vorlage. */
  final?: boolean
}

export function DailyRewardsChart({ daily, period, final }: Props) {
  const { t } = useTranslation('reports')
  const isEmpty = daily.length === 0
  const isDataGap = period ? isPeriodInRewardDataGap(period) : false
  const chartData = useMemo(() => {
    if (period) return buildFullMonthSeries(daily, period)
    return [...daily].sort((a, b) => a.date.localeCompare(b.date))
  }, [daily, period])

  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="chart-header-row">
        <div>
          <h3 className="section-heading" style={{ marginBottom: 0 }}>
            {t('chart.title')}
          </h3>
          <p className="muted" style={{ fontSize: 11, margin: '2px 0 0' }}>{t('chart.subtitle')}</p>
        </div>
        {final !== undefined && (
          <span className={final ? 'pill pill-success' : 'pill'}>
            {final ? t('chart.final') : t('chart.inProgress')}
          </span>
        )}
      </div>

      {isDataGap && <RewardDataGapNotice />}

      {isEmpty ? (
        <div
          style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          aria-label={t('chart.empty.ariaLabel')}
        >
          <span className="muted">{t('chart.empty.message')}</span>
        </div>
      ) : (
        <div aria-label={t('chart.ariaLabel')} style={{ marginTop: 8 }}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                tickLine={false}
                axisLine={false}
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                tickFormatter={(v: number) => v.toFixed(2)}
                tickLine={false}
                axisLine={false}
                domain={['auto', 'auto']}
              />
              <Tooltip
                content={<ChartTooltip />}
                cursor={{ stroke: 'var(--border)', strokeWidth: 1, strokeDasharray: '4 4' }}
              />
              <Line
                type="monotone"
                dataKey="rewards_hnt"
                stroke={HNT_COLOR}
                dot={false}
                activeDot={{ r: 4, fill: HNT_COLOR, stroke: 'var(--bg)' }}
                strokeWidth={2}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
